import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BookOpen, Compass, ArrowLeftRight, Users, Building2,
  Check, ArrowLeft, ArrowUp, ArrowRight, Library,
  Smartphone,
} from 'lucide-react';
import Logo from '../components/Logo';

const LIVROS_COVERFLOW = [
  '9780451524935',  // 1984 — Signet Classics
  '9780439708180',  // Harry Potter e a Pedra Filosofal — Scholastic
  '9780062315007',  // The Alchemist — HarperOne
  '9780062316097',  // Sapiens — Harper Perennial
  '9780441013593',  // Duna — Ace Books
  '9780060883287',  // Cem Anos de Solidão — Avon
  '9780156013987',  // O Pequeno Príncipe — Harvest/HBJ
];

const capaOL = (isbn) => `https://covers.openlibrary.org/b/isbn/${isbn}-M.jpg`;

const CF = {
  0: { w: 110, h: 160, ry: 0,  z: 10, op: 1,    shadow: '0 12px 32px rgba(0,0,0,0.22)' },
  1: { w: 82,  h: 120, ry: 28, z: 8,  op: 0.88, shadow: '0 8px 20px rgba(0,0,0,0.15)'  },
  2: { w: 62,  h: 90,  ry: 52, z: 6,  op: 0.6,  shadow: '0 4px 12px rgba(0,0,0,0.10)'  },
  3: { w: 46,  h: 68,  ry: 72, z: 4,  op: 0,    shadow: 'none'                          },
};

function CoverflowHero() {
  const capas = LIVROS_COVERFLOW.map(capaOL);
  const [imgErro, setImgErro] = useState({});
  const [current, setCurrent] = useState(0);
  const [resetKey, setResetKey] = useState(0);
  const n = LIVROS_COVERFLOW.length;

  useEffect(() => {
    const timer = setInterval(() => setCurrent(c => (c + 1) % n), 2200);
    return () => clearInterval(timer);
  }, [resetKey, n]);

  function goTo(idx) {
    setCurrent(idx);
    setResetKey(k => k + 1);
  }

  return (
    <div className="flex flex-col items-center gap-5 w-full select-none">
      <div className="relative w-full h-[200px] md:h-[260px]" style={{ perspective: 600 }}>
        {LIVROS_COVERFLOW.map((_, i) => {
          let offset = i - current;
          if (offset > n / 2)  offset -= n;
          if (offset < -n / 2) offset += n;
          const abs = Math.min(Math.abs(offset), 3);
          const cfg = CF[abs];
          const ry = offset === 0 ? 0 : offset < 0 ? cfg.ry : -cfg.ry;
          const isCenter = offset === 0;

          return (
            <div
              key={i}
              onClick={() => goTo(i)}
              style={{
                position: 'absolute',
                width: cfg.w,
                height: cfg.h,
                left: `calc(50% + ${offset * 95}px - ${cfg.w / 2}px)`,
                top: `calc(50% - ${cfg.h / 2}px)`,
                transform: `perspective(600px) rotateY(${ry}deg)`,
                zIndex: cfg.z,
                opacity: cfg.op,
                boxShadow: cfg.shadow,
                borderRadius: 6,
                overflow: 'hidden',
                cursor: isCenter ? 'default' : 'pointer',
                transition: 'all 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                animation: isCenter ? 'floatBook 3.5s ease-in-out infinite' : 'none',
              }}
            >
              {!imgErro[i] ? (
                <img src={capas[i]} alt=""
                  onError={() => setImgErro(e => ({ ...e, [i]: true }))}
                  style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
              ) : (
                <div style={{ width: '100%', height: '100%', background: '#EDE9FF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <BookOpen size={cfg.h > 100 ? 24 : 16} strokeWidth={1.5} color="#9A93A4" />
                </div>
              )}
            </div>
          );
        })}
        <style>{`
          @keyframes floatBook {
            0%, 100% { transform: perspective(600px) rotateY(0deg) translateY(0px); }
            50%       { transform: perspective(600px) rotateY(0deg) translateY(-8px); }
          }
          @keyframes floatCard {
            0%, 100% { transform: translateY(0px); }
            50%       { transform: translateY(-6px); }
          }
        `}</style>
      </div>
      <div className="flex items-center gap-2">
        {LIVROS_COVERFLOW.map((_, i) => (
          <button
            key={i}
            onClick={() => goTo(i)}
            style={{
              width:  i === current ? 20 : 6,
              height: 6,
              borderRadius: i === current ? 3 : '50%',
              background: i === current ? '#6B4EFF' : '#d0d0d0',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              transition: 'all 0.35s ease',
            }}
          />
        ))}
      </div>
    </div>
  );
}

function ExplorarDemo() {
  const [imgErroDemo, setImgErroDemo] = useState(false);
  const capaDemo = capaOL('9780553213690'); // The Metamorphosis — Bantam Books

  return (
    <div className="flex flex-col items-center w-full max-w-[280px] mx-auto">
      <div className="relative w-[220px] h-[290px] mb-8">
        <div className="absolute inset-0 rounded-[20px]"
          style={{ transform: 'rotate(6deg) translateY(10px)', background: 'rgba(255,255,255,0.08)', zIndex: 1 }} />
        <div className="absolute inset-0 rounded-[20px]"
          style={{ transform: 'rotate(-3deg) translateY(5px)', background: 'rgba(255,255,255,0.13)', zIndex: 2 }} />
        <div className="absolute inset-0 rounded-[20px] overflow-hidden bg-white"
          style={{ zIndex: 3, boxShadow: '0 24px 60px rgba(0,0,0,0.4)', animation: 'floatCard 3.5s ease-in-out infinite' }}>
          <div className="h-[175px] overflow-hidden"
            style={imgErroDemo ? { background: 'linear-gradient(145deg, #5B3CE4, #2E1B8A)', display: 'flex', alignItems: 'center', justifyContent: 'center' } : {}}>
            {!imgErroDemo
              ? <img src={capaDemo} alt="A Metamorfose"
                  onError={() => setImgErroDemo(true)}
                  style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
              : <BookOpen size={52} strokeWidth={1} color="rgba(255,255,255,0.55)" />
            }
          </div>
          <div className="px-4 py-3">
            <p className="font-extrabold text-[14px] text-ink m-0">A Metamorfose</p>
            <p className="text-[12px] text-ink-400 m-0 mt-0.5">Franz Kafka</p>
            <div className="flex items-center gap-1.5 mt-2">
              <div className="w-5 h-5 rounded-full flex items-center justify-center text-[10px]"
                style={{ background: '#EDE9FF', color: '#5B3CE4', fontWeight: 700 }}>M</div>
              <p className="text-[11px] text-ink-400 m-0">disponível com @maria</p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3 w-full">
        {[
          { Icon: ArrowLeft,  label: 'Passar',  sub: 'Não tenho interesse',    bg: 'rgba(255,80,80,0.15)',    tc: '#FF9090', ic: 'rgba(255,80,80,0.25)' },
          { Icon: ArrowUp,    label: 'Pedir',   sub: 'Quero pegar emprestado', bg: 'rgba(31,169,113,0.15)',   tc: '#5DEBB7', ic: 'rgba(31,169,113,0.25)' },
          { Icon: ArrowRight, label: 'Salvar',  sub: 'Futuras leituras',       bg: 'rgba(91,60,228,0.25)',    tc: '#B0A0FF', ic: 'rgba(91,60,228,0.35)' },
        ].map(({ Icon, label, sub, bg, tc, ic }, i) => (
          <div key={i} className="flex flex-col items-center text-center rounded-[12px] py-3 px-2"
            style={{ background: bg }}>
            <div className="w-8 h-8 rounded-full flex items-center justify-center mb-1.5"
              style={{ background: ic }}>
              <Icon size={15} strokeWidth={2.5} color={tc} />
            </div>
            <p className="text-[11px] font-extrabold m-0" style={{ color: 'rgba(255,255,255,0.9)' }}>{label}</p>
            <p className="text-[10px] m-0 mt-0.5 leading-tight" style={{ color: 'rgba(255,255,255,0.5)' }}>{sub}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function LandingPage() {
  const navigate = useNavigate();
  const [installPrompt, setInstallPrompt] = useState(null);
  const [instalado, setInstalado] = useState(false);
  const [dicaIOS, setDicaIOS] = useState(false);

  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;

  useEffect(() => {
    const handler = (e) => { e.preventDefault(); setInstallPrompt(e); };
    window.addEventListener('beforeinstallprompt', handler);
    window.addEventListener('appinstalled', () => setInstalado(true));
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  async function handleInstall() {
    if (isIOS) { setDicaIOS(true); return; }
    if (!installPrompt) return;
    installPrompt.prompt();
    const { outcome } = await installPrompt.userChoice;
    if (outcome === 'accepted') setInstalado(true);
    setInstallPrompt(null);
  }

  const mostrarBotao = !isStandalone && !instalado && (installPrompt || isIOS);

  return (
    <div className="min-h-screen bg-paper text-ink font-sans">

      {/* ── NAVBAR ─────────────────────────────────────────────── */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-line-soft pt-safe-nav">
        <div className="max-w-5xl mx-auto px-5 h-14 flex items-center justify-between">
          <Logo size="md" />
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/login')}
              className="text-sm font-medium px-4 py-1.5 rounded-xl border border-line text-ink hover:bg-paper transition-colors">
              Entrar
            </button>
            <button onClick={() => navigate('/cadastro')}
              className="text-sm font-medium px-4 py-1.5 rounded-xl text-white hover:opacity-90 transition-opacity border-0 cursor-pointer"
              style={{ background: '#5B3CE4' }}>
              Criar conta grátis
            </button>
          </div>
        </div>
      </nav>

      {/* ── HERO ───────────────────────────────────────────────── */}
      <section className="pt-28 pb-20 px-5"
        style={{ background: 'linear-gradient(160deg, #EDE9FF 0%, #F7F5FF 40%, #FAF8F5 100%)' }}>
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center gap-10 md:gap-16">

          <div className="flex-1 text-center md:text-left">
            <span className="inline-block text-xs font-semibold px-3 py-1.5 rounded-full mb-6"
              style={{ background: '#EDE9FF', color: '#5B3CE4' }}>
              Para leitores que acreditam que livros existem para circular
            </span>
            <h1 className="text-[42px] md:text-[58px] font-extrabold leading-[1.08] mb-5 text-ink tracking-tight">
              Livro bom é livro<br />
              <span style={{ color: '#5B3CE4' }}>fora da estante.</span>
            </h1>
            <p className="text-base md:text-lg leading-relaxed mb-8 max-w-md mx-auto md:mx-0"
              style={{ color: '#6B6480' }}>
              Organize seu acervo, empreste para sua rede e descubra livros disponíveis perto de você.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center md:justify-start">
              <button onClick={() => navigate('/cadastro')}
                className="text-[15px] font-extrabold px-8 py-3.5 rounded-2xl text-white border-0 cursor-pointer hover:opacity-90 transition-opacity"
                style={{ background: '#5B3CE4', boxShadow: '0 8px 24px rgba(91,60,228,0.32)' }}>
                Criar conta grátis
              </button>
              <button onClick={() => navigate('/login')}
                className="text-[15px] font-medium px-8 py-3.5 rounded-2xl border border-line text-ink hover:bg-white transition-colors cursor-pointer bg-transparent">
                Já tenho conta
              </button>
            </div>
            {mostrarBotao && (
              <button onClick={handleInstall}
                className="flex items-center gap-2 text-sm font-medium px-5 py-2.5 rounded-2xl border-2 border-brand hover:bg-brand-light transition-colors w-fit mx-auto md:mx-0 mt-3 bg-transparent cursor-pointer"
                style={{ borderColor: '#5B3CE4', color: '#5B3CE4' }}>
                <Smartphone size={16} strokeWidth={1.8} />
                Instalar app no celular
              </button>
            )}
            {instalado && (
              <p className="text-sm text-green-600 font-medium mt-3 mx-auto md:mx-0">
                ✓ App instalado com sucesso!
              </p>
            )}
          </div>

          <div className="w-full md:w-[360px] flex-shrink-0 flex justify-center items-center">
            <CoverflowHero />
          </div>
        </div>
      </section>

      {/* ── COMO FUNCIONA ──────────────────────────────────────── */}
      <section className="py-20 px-5 bg-white">
        <div className="max-w-4xl mx-auto">
          <p className="text-center text-[11px] font-extrabold tracking-widest uppercase mb-3"
            style={{ color: '#5B3CE4' }}>Como funciona</p>
          <h2 className="text-center text-[30px] font-extrabold mb-4 text-ink tracking-tight">
            Três passos. Um ecossistema.
          </h2>
          <p className="text-center text-[15px] mb-14 max-w-md mx-auto" style={{ color: '#8A86A0' }}>
            Do acervo pessoal à comunidade de leitores — tudo conectado.
          </p>

          <div className="grid md:grid-cols-3 gap-10">
            {[
              {
                num: '01',
                Icon: BookOpen,
                titulo: 'Organize',
                desc: 'Adicione seus livros com capa, avaliação e notas pessoais. Sua estante digital, sempre no bolso.',
              },
              {
                num: '02',
                Icon: Compass,
                titulo: 'Descubra',
                desc: 'Explore livros disponíveis na sua rede. Deslize, peça emprestado ou salve para ler depois.',
              },
              {
                num: '03',
                Icon: ArrowLeftRight,
                titulo: 'Empreste',
                desc: 'Controle quem está com seus livros, defina prazos e acompanhe cada empréstimo com notificações em tempo real.',
              },
            ].map(({ num, Icon, titulo, desc }, i) => (
              <div key={i} className="relative">
                <p className="text-[64px] font-extrabold leading-none mb-4 select-none"
                  style={{ color: '#EDE9FF' }}>{num}</p>
                <div className="w-11 h-11 rounded-[13px] flex items-center justify-center mb-4"
                  style={{ background: '#EDE9FF' }}>
                  <Icon size={22} strokeWidth={1.6} color="#5B3CE4" />
                </div>
                <h3 className="text-[18px] font-extrabold mb-2 text-ink">{titulo}</h3>
                <p className="text-[14px] leading-relaxed" style={{ color: '#8A86A0' }}>{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── EXPLORAR SPOTLIGHT ─────────────────────────────────── */}
      <section className="py-20 px-5"
        style={{ background: 'linear-gradient(145deg, #120A30 0%, #1E1060 50%, #2A1580 100%)' }}>
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center gap-12 md:gap-16">

          <div className="flex-1 text-center md:text-left order-2 md:order-1">
            <span className="inline-block text-[11px] font-extrabold tracking-widest uppercase px-3 py-1.5 rounded-full mb-5"
              style={{ background: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.7)' }}>
              Funcionalidade Explorar
            </span>
            <h2 className="text-[30px] md:text-[38px] font-extrabold text-white mb-4 leading-tight tracking-tight">
              Livros disponíveis<br />perto de você, agora.
            </h2>
            <p className="text-[15px] mb-8 leading-relaxed max-w-md mx-auto md:mx-0"
              style={{ color: 'rgba(255,255,255,0.6)' }}>
              Veja o acervo de amigos e da sua comunidade. Deslize para descobrir, salvar nas suas futuras leituras ou pedir emprestado na hora.
            </p>

            <div className="space-y-3 max-w-sm mx-auto md:mx-0">
              {[
                { Icon: ArrowLeft,  label: 'Não tenho interesse — passe para o próximo', bg: 'rgba(255,80,80,0.12)',   tc: '#FF9090', bc: 'rgba(255,80,80,0.25)' },
                { Icon: ArrowRight, label: 'Salvar em Futuras Leituras',                  bg: 'rgba(91,60,228,0.2)',   tc: '#B0A0FF', bc: 'rgba(91,60,228,0.35)' },
                { Icon: ArrowUp,    label: 'Quero pedir emprestado agora!',               bg: 'rgba(31,169,113,0.15)', tc: '#5DEBB7', bc: 'rgba(31,169,113,0.28)' },
              ].map(({ Icon, label, bg, tc, bc }, i) => (
                <div key={i} className="flex items-center gap-3 rounded-[14px] px-4 py-3"
                  style={{ background: bg }}>
                  <div className="w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center"
                    style={{ background: bc }}>
                    <Icon size={15} strokeWidth={2.5} color={tc} />
                  </div>
                  <span className="text-[14px] font-medium" style={{ color: 'rgba(255,255,255,0.85)' }}>{label}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="flex-shrink-0 order-1 md:order-2 w-full md:w-auto flex justify-center">
            <ExplorarDemo />
          </div>
        </div>
      </section>

      {/* ── FEATURES GRID ──────────────────────────────────────── */}
      <section className="py-20 px-5" style={{ background: '#FAF8F5' }}>
        <div className="max-w-4xl mx-auto">
          <p className="text-center text-[11px] font-extrabold tracking-widest uppercase mb-3"
            style={{ color: '#5B3CE4' }}>Funcionalidades</p>
          <h2 className="text-center text-[30px] font-extrabold mb-4 text-ink tracking-tight">
            Tudo num só lugar
          </h2>
          <p className="text-center text-[15px] mb-12 max-w-md mx-auto" style={{ color: '#8A86A0' }}>
            Cada parte do app foi pensada para que os livros circulem.
          </p>

          <div className="grid sm:grid-cols-2 gap-5">
            {[
              {
                Icon: BookOpen,
                titulo: 'Acervo pessoal',
                desc: 'Registre seus livros com capa, avaliação e notas. Compartilhe sua estante com amigos ou mantenha privada.',
                color: '#EDE9FF',
                iconColor: '#5B3CE4',
              },
              {
                Icon: ArrowLeftRight,
                titulo: 'Empréstimos com controle',
                desc: 'Aprove ou recuse pedidos, defina prazos, receba notificações. Saiba sempre onde está cada livro seu.',
                color: '#E8F8F0',
                iconColor: '#1FA971',
              },
              {
                Icon: Users,
                titulo: 'Rede de leitores',
                desc: 'Siga amigos, veja suas estantes e peça livros diretamente. Cada conexão expande o que você pode ler.',
                color: '#FFF4E0',
                iconColor: '#E08A00',
              },
              {
                Icon: Building2,
                titulo: 'Clubes de leitura',
                desc: 'Crie grupos com amigos, compartilhe acervos e combine leituras. Livros que circulam, conversas que acontecem.',
                color: '#FFE9E9',
                iconColor: '#D94040',
              },
            ].map(({ Icon, titulo, desc, color, iconColor }, i) => (
              <div key={i} className="bg-white rounded-[20px] p-6 border hover:shadow-md transition-shadow"
                style={{ borderColor: '#EBE8F0' }}>
                <div className="w-11 h-11 rounded-[13px] flex items-center justify-center mb-4"
                  style={{ background: color }}>
                  <Icon size={22} strokeWidth={1.6} color={iconColor} />
                </div>
                <h3 className="text-[16px] font-extrabold mb-2 text-ink">{titulo}</h3>
                <p className="text-[13.5px] leading-relaxed" style={{ color: '#8A86A0' }}>{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FILOSOFIA / QUOTE ──────────────────────────────────── */}
      <section className="py-20 px-5" style={{ background: '#EDE9FF' }}>
        <div className="max-w-2xl mx-auto text-center">
          <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-6"
            style={{ background: 'rgba(91,60,228,0.12)' }}>
            <BookOpen size={28} strokeWidth={1.5} color="#5B3CE4" />
          </div>
          <blockquote className="text-[22px] md:text-[28px] font-extrabold text-ink leading-snug mb-6 tracking-tight">
            "Por meio de livros podemos ter acesso a pessoas que não estão mais entre nós, a pensamentos inacessíveis nos nossos ciclos, a fantasias além da nossa imaginação, e a romances que nunca viveremos."
          </blockquote>
          <p className="text-[15px] font-semibold" style={{ color: '#5B3CE4' }}>
            É por isso que livro bom é livro fora da estante.
          </p>
        </div>
      </section>

      {/* ── PRICING ────────────────────────────────────────────── */}
      <section className="py-20 px-5 bg-white">
        <div className="max-w-2xl mx-auto">
          <p className="text-center text-[11px] font-extrabold tracking-widest uppercase mb-3"
            style={{ color: '#5B3CE4' }}>Planos</p>
          <h2 className="text-center text-[30px] font-extrabold mb-2 text-ink tracking-tight">Simples assim</h2>
          <p className="text-center text-[15px] mb-10" style={{ color: '#8A86A0' }}>
            Comece grátis. Pague uma vez quando quiser mais.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div className="rounded-[24px] p-6 border" style={{ background: '#FAF8F5', borderColor: '#EBE8F0' }}>
              <p className="text-[11px] font-extrabold tracking-widest uppercase mb-1" style={{ color: '#8A86A0' }}>Grátis</p>
              <p className="text-[38px] font-extrabold text-ink mb-0.5">R$ 0</p>
              <p className="text-[13px] mb-6" style={{ color: '#8A86A0' }}>Para sempre</p>
              <ul className="space-y-3 mb-6 list-none p-0 m-0">
                {[
                  'Até 20 livros no acervo',
                  'Participar de até 2 clubes',
                  '5 sugestões de leitura por mês',
                  '10 swipes por dia no Explorar',
                  'Até 3 empréstimos simultâneos',
                ].map(f => (
                  <li key={f} className="flex items-center gap-2.5 text-[13.5px]" style={{ color: '#5A5568' }}>
                    <span className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ background: '#E8F8F0' }}>
                      <Check size={11} strokeWidth={2.5} color="#1FA971" />
                    </span>
                    {f}
                  </li>
                ))}
              </ul>
              <button onClick={() => navigate('/cadastro')}
                className="w-full py-3 rounded-[14px] text-[14px] font-extrabold border cursor-pointer"
                style={{ background: '#FAF8F5', borderColor: '#DDD9E8', color: '#5B3CE4' }}>
                Começar grátis
              </button>
            </div>

            <div className="rounded-[24px] p-6 relative overflow-hidden"
              style={{ background: 'linear-gradient(145deg, #5B3CE4, #3F27B0)', boxShadow: '0 16px 40px rgba(91,60,228,0.30)' }}>
              <span className="absolute top-4 right-4 text-[11px] font-extrabold px-2.5 py-1 rounded-full tracking-wider"
                style={{ background: '#FFAA00', color: '#1A1108' }}>PREMIUM</span>
              <p className="text-[11px] font-extrabold tracking-widest uppercase mb-1" style={{ color: 'rgba(255,255,255,0.6)' }}>Premium</p>
              <p className="text-[38px] font-extrabold text-white mb-0">R$ 29,90</p>
              <p className="text-[13px] mb-6" style={{ color: 'rgba(255,255,255,0.6)' }}>Pagamento único · Acesso vitalício</p>
              <ul className="space-y-3 mb-6 list-none p-0 m-0">
                {[
                  'Livros ilimitados no acervo',
                  'Clubes ilimitados',
                  'Sugestões de leitura ilimitadas',
                  'Explorar sem limite diário',
                  'Empréstimos simultâneos ilimitados',
                ].map(f => (
                  <li key={f} className="flex items-center gap-2.5 text-[13.5px] text-white">
                    <span className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ background: 'rgba(255,255,255,0.2)' }}>
                      <Check size={11} strokeWidth={2.5} color="#fff" />
                    </span>
                    {f}
                  </li>
                ))}
              </ul>
              <button onClick={() => navigate('/cadastro')}
                className="w-full py-3 rounded-[14px] text-[14px] font-extrabold border-0 cursor-pointer"
                style={{ background: '#FFAA00', color: '#1A1108', boxShadow: '0 6px 16px rgba(0,0,0,0.20)' }}>
                Comprar agora
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA FINAL ──────────────────────────────────────────── */}
      <section className="py-24 px-5 text-center"
        style={{ background: 'linear-gradient(160deg, #EDE9FF 0%, #FAF8F5 100%)' }}>
        <div className="max-w-lg mx-auto">
          <div className="w-16 h-16 rounded-[20px] flex items-center justify-center mx-auto mb-6"
            style={{ background: '#EDE9FF' }}>
            <Library size={32} strokeWidth={1.4} color="#5B3CE4" />
          </div>
          <h2 className="text-[30px] md:text-[36px] font-extrabold mb-3 text-ink tracking-tight leading-snug">
            Sua estante está<br />esperando.
          </h2>
          <p className="text-[15px] mb-8 leading-relaxed max-w-sm mx-auto" style={{ color: '#8A86A0' }}>
            Cada livro que você adiciona é uma porta aberta para alguém que ainda não encontrou aquela leitura.
          </p>
          <button onClick={() => navigate('/cadastro')}
            className="text-[15px] font-extrabold px-9 py-4 rounded-2xl text-white border-0 cursor-pointer hover:opacity-90 transition-opacity"
            style={{ background: '#5B3CE4', boxShadow: '0 10px 28px rgba(91,60,228,0.35)' }}>
            Começar agora — é grátis
          </button>
          <p className="text-[12px] mt-4" style={{ color: '#A09AB8' }}>
            Sem cartão de crédito. Sem compromisso.
          </p>
        </div>
      </section>

      {/* ── FOOTER ─────────────────────────────────────────────── */}
      <footer className="py-8 px-5 text-center border-t" style={{ borderColor: '#EBE8F0' }}>
        <div className="flex justify-center mb-3"><Logo size="sm" /></div>
        <p className="text-[12px]" style={{ color: '#A09AB8' }}>
          Feito com carinho para leitores que acreditam na cultura da troca.
        </p>
      </footer>

      {/* ── MODAL DICA iOS ─────────────────────────────────────── */}
      {dicaIOS && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 px-4 pb-6"
          onClick={() => setDicaIOS(false)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-xl"
            onClick={e => e.stopPropagation()}>
            <p className="font-bold text-ink mb-4 text-base">Instalar no iPhone</p>
            <ol className="space-y-3 text-sm text-ink-500">
              <li className="flex items-start gap-3">
                <span className="text-xl shrink-0">1️⃣</span>
                <span>Toque no botão de compartilhar <span className="inline-block bg-paper-2 px-1.5 py-0.5 rounded text-xs font-mono">⎙</span> na barra inferior do Safari</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-xl shrink-0">2️⃣</span>
                <span>Role a lista e toque em <strong>"Adicionar à Tela de Início"</strong></span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-xl shrink-0">3️⃣</span>
                <span>Toque em <strong>Adicionar</strong> no canto superior direito</span>
              </li>
            </ol>
            <button onClick={() => setDicaIOS(false)}
              className="mt-6 w-full py-3 rounded-xl text-white text-sm font-semibold border-0 cursor-pointer"
              style={{ background: '#5B3CE4' }}>
              Entendi
            </button>
          </div>
        </div>
      )}

    </div>
  );
}

export default LandingPage;
